import { datadogLogs } from '@datadog/browser-logs'
import moment from 'moment'

import { stringToDate } from 'modules/core/lib/dates'
import type { OrgChartPageQuery } from 'generated/resources/graphql'

export type GraphQLEmployee = NonNullable<OrgChartPageQuery['employees']>['employees'][number]

/**
 * Check if an employee is active (not terminated or terminated in the future)
 */
const isActive = (employee: GraphQLEmployee): boolean => {
  if (!employee.terminatedOn) return true

  return stringToDate(employee.terminatedOn, 'backend').isAfter(moment(), 'day')
}

/**
 * Check if an employee has a contract
 */
const hasContract = (employee: GraphQLEmployee): boolean => {
  return !!employee.referenceContract
}

/**
 * Filter employees that are active and have at least one contract version
 */
export const getActiveEmployeesWithContract = (employees: GraphQLEmployee[]): GraphQLEmployee[] => {
  return employees.filter((employee) => isActive(employee) && hasContract(employee))
}

/**
 * Trace the path from an employee to the root of the hierarchy
 * Returns an array of manager IDs from the immediate manager to the root
 */
const tracePath = (
  managerId: number | null | undefined,
  employee: GraphQLEmployee,
  employeeMap: Map<number, GraphQLEmployee>
): number[] => {
  const idTreePath: number[] = []
  const visitedManagerIds = new Set<number>()

  let currentManagerId: number | null | undefined = managerId

  while (currentManagerId) {
    if (visitedManagerIds.has(currentManagerId)) {
      const data = {
        message: 'Infinite loop detected in tracePath',
        employee_id: employee.id,
        manager_id: currentManagerId,
      }

      datadogLogs.logger.log('Circular reference', data)
      break
    }
    visitedManagerIds.add(currentManagerId)

    const manager = employeeMap.get(currentManagerId)

    if (!manager) break

    idTreePath.push(manager.id)
    currentManagerId = manager.managerId
  }
  return idTreePath
}

/**
 * Check if an employee is an orphan (has a manager that doesn't exist in the tree)
 */
export const isOrphan = (
  employee: GraphQLEmployee,
  employeeMap: Map<number, GraphQLEmployee>
): boolean => {
  const managerId = employee.managerId
  if (!managerId) {
    return false
  }

  const paths = tracePath(managerId, employee, employeeMap)

  if (paths.length === 0) {
    return true
  }

  const parentId = paths[paths.length - 1]
  if (parentId === undefined) {
    return true
  }

  const manager = employeeMap.get(parentId)

  return !manager
}

/**
 * Get all employees hierarchically managed by a given employee
 */
export const peopleHierarchicallyManagedByEmployee = (
  employee: GraphQLEmployee,
  allEmployees: GraphQLEmployee[],
  employeeMap: Map<number, GraphQLEmployee>
): GraphQLEmployee[] => {
  return allEmployees.filter((employeeWithContract) => {
    const managerId = employeeWithContract.managerId
    if (!managerId) {
      return false
    }

    const idPath = tracePath(managerId, employee, employeeMap)

    if (idPath.length === 0) {
      return false
    }

    const lastIdInPath = idPath[idPath.length - 1]

    return employee.id === lastIdInPath
  })
}

/**
 * Get employees directly managed by a given employee
 */
export const employeeDirectlyManagedEmployees = (
  employee: GraphQLEmployee,
  allEmployees: GraphQLEmployee[]
): GraphQLEmployee[] => {
  return allEmployees.filter((emp) => emp.managerId === employee.id)
}

/**
 * Get all employees managed by a given employee (direct and indirect)
 */
export const employeeManagedEmployees = (
  employee: GraphQLEmployee,
  allEmployees: GraphQLEmployee[]
): GraphQLEmployee[] => {
  const queue: GraphQLEmployee[] = [employee]
  const allManagedEmployees: GraphQLEmployee[] = []
  const visitedEmployeeIds = new Set<number>()

  while (queue.length > 0) {
    const currentEmployee = queue.shift()
    if (!currentEmployee) continue

    const employeeId = currentEmployee.id
    if (visitedEmployeeIds.has(employeeId)) continue
    visitedEmployeeIds.add(employeeId)

    const directManagedEmployees = allEmployees.filter(
      (managedEmployee) => managedEmployee.managerId === employeeId
    )

    allManagedEmployees.push(...directManagedEmployees)
    queue.push(...directManagedEmployees)
  }
  return allManagedEmployees
}
